import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DocumentoProcesso,
  ProcessoArbitralDetail,
  MensagemChatItem,
  SituacaoAtiva,
  SolicitacaoAlteracao,
  TypingUser,
} from '../types/arbitralDetailTypes';
import {
  createParteArbitral,
  fetchProcessoArbitral,
  patchParteContato,
  reenviarNotificacaoBounce,
  reabrirProcessoArbitral,
  resolverPendencia,
  reenviarPendencia,
  fetchSituacoesDisponiveis,
  vincularSituacaoArbitral,
  desvincularSituacaoArbitral,
  fetchDocumentosArbitral,
  removerDocumentoArbitral,
  fetchAndamentosArbitral,
  criarAndamentoArbitral,
  fetchMensagensChat,
  enviarMensagemChat,
  marcarMensagemLida,
  fetchNaoLidasChat,
  fetchSolicitacoesArbitral,
  criarSolicitacaoArbitral,
  cancelarSolicitacaoArbitral,
  cancelarProcessoArbitral,
} from './actions';

interface ArbitralDetalhesState {
  processo: ProcessoArbitralDetail | null;
  // Lista completa dos documentos (carregada por fetchDocumentosArbitral). Fica separada de
  // `processo.documentos` porque o detalhe traz um shape reduzido que sobrescreveria os campos
  // ricos (autor, tipoMime, tamanho) a cada recarga/ação que devolve o processo.
  documentos: DocumentoProcesso[];
  mensagensChat: MensagemChatItem[];
  loading: boolean;
  error: string | null;
  creatingParte: boolean;
  patchingParteId: string | null;
  reenvioParteId: string | null;
  submittingPendenciaId: string | null;
  loadingDocumentos: boolean;
  removingDocumentoId: string | null;
  loadingAndamentos: boolean;
  errorAndamentos: string | null;
  submittingAndamento: boolean;
  loadingChat: boolean;
  sendingMessage: boolean;
  typingUsers: TypingUser[];
  reabrindo: boolean;
  situacoesDisponiveis: SituacaoAtiva[];
  gerenciandoSituacao: boolean;
  solicitacoes: SolicitacaoAlteracao[];
  loadingSolicitacoes: boolean;
  submittingSolicitacao: boolean;
  cancelandoProcesso: boolean;
}

const initialState: ArbitralDetalhesState = {
  processo: null,
  documentos: [],
  mensagensChat: [],
  loading: false,
  error: null,
  creatingParte: false,
  patchingParteId: null,
  reenvioParteId: null,
  submittingPendenciaId: null,
  loadingDocumentos: false,
  removingDocumentoId: null,
  loadingAndamentos: false,
  errorAndamentos: null,
  submittingAndamento: false,
  loadingChat: false,
  sendingMessage: false,
  typingUsers: [],
  reabrindo: false,
  situacoesDisponiveis: [],
  gerenciandoSituacao: false,
  solicitacoes: [],
  loadingSolicitacoes: false,
  submittingSolicitacao: false,
  cancelandoProcesso: false,
};

const arbitralDetalhesSlice = createSlice({
  name: 'arbitralDetalhes',
  initialState,
  reducers: {
    clearProcesso(state) {
      state.processo = null;
      state.mensagensChat = [];
      state.error = null;
    },
    appendMensagem(state, action: PayloadAction<MensagemChatItem>) {
      const alreadyExists = state.mensagensChat.some((m) => m.id === action.payload.id);
      if (!alreadyExists) {
        state.mensagensChat.push(action.payload);
      }
    },
    setTyping(state, action: PayloadAction<TypingUser>) {
      const idx = state.typingUsers.findIndex((u) => u.userId === action.payload.userId);
      if (idx >= 0) {
        state.typingUsers[idx] = action.payload;
      } else {
        state.typingUsers.push(action.payload);
      }
    },
    clearStaleTypingUsers(state) {
      if (state.typingUsers.length === 0) return;
      const cutoff = Date.now() - 3000;
      const filtered = state.typingUsers.filter((u) => Date.parse(u.timestamp) > cutoff);
      if (filtered.length !== state.typingUsers.length) {
        state.typingUsers = filtered;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProcessoArbitral.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Novo processo sendo carregado — descarta documentos do processo anterior.
        state.documentos = [];
      })
      .addCase(fetchProcessoArbitral.fulfilled, (state, action) => {
        state.loading = false;
        state.processo = action.payload;
      })
      .addCase(fetchProcessoArbitral.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Erro ao carregar processo';
      })
      .addCase(createParteArbitral.pending, (state) => {
        state.creatingParte = true;
      })
      .addCase(createParteArbitral.fulfilled, (state, action) => {
        state.creatingParte = false;
        state.processo = action.payload;
      })
      .addCase(createParteArbitral.rejected, (state) => {
        state.creatingParte = false;
      })
      .addCase(patchParteContato.pending, (state, action) => {
        state.patchingParteId = action.meta.arg.parteId;
      })
      .addCase(patchParteContato.fulfilled, (state, action) => {
        state.patchingParteId = null;
        if (state.processo) {
          const parte = state.processo.partes.find((p) => p.id === action.meta.arg.parteId);
          if (parte) {
            parte.email = action.meta.arg.data.email;
            parte.telefone = action.meta.arg.data.telefone;
          }
        }
      })
      .addCase(patchParteContato.rejected, (state) => {
        state.patchingParteId = null;
      })
      .addCase(reenviarNotificacaoBounce.pending, (state, action) => {
        state.reenvioParteId = action.meta.arg.parteId;
      })
      .addCase(reenviarNotificacaoBounce.fulfilled, (state) => {
        state.reenvioParteId = null;
      })
      .addCase(reenviarNotificacaoBounce.rejected, (state) => {
        state.reenvioParteId = null;
      })
      .addCase(reabrirProcessoArbitral.pending, (state) => {
        state.reabrindo = true;
      })
      .addCase(reabrirProcessoArbitral.fulfilled, (state) => {
        state.reabrindo = false;
      })
      .addCase(reabrirProcessoArbitral.rejected, (state) => {
        state.reabrindo = false;
      })
      .addCase(resolverPendencia.pending, (state, action) => {
        state.submittingPendenciaId = action.meta.arg.pendenciaId;
      })
      .addCase(resolverPendencia.fulfilled, (state, action) => {
        state.submittingPendenciaId = null;
        if (state.processo) {
          const p = state.processo.pendencias.find((x) => x.id === action.payload.pendenciaId);
          if (p) p.status = 'AGUARDANDO_VALIDACAO';
        }
      })
      .addCase(resolverPendencia.rejected, (state) => {
        state.submittingPendenciaId = null;
      })
      .addCase(reenviarPendencia.pending, (state, action) => {
        state.submittingPendenciaId = action.meta.arg.pendenciaId;
      })
      .addCase(reenviarPendencia.fulfilled, (state, action) => {
        state.submittingPendenciaId = null;
        if (state.processo) {
          const p = state.processo.pendencias.find((x) => x.id === action.payload.pendenciaId);
          if (p) p.status = 'AGUARDANDO_VALIDACAO';
        }
      })
      .addCase(reenviarPendencia.rejected, (state) => {
        state.submittingPendenciaId = null;
      })
      .addCase(fetchSituacoesDisponiveis.fulfilled, (state, action) => {
        state.situacoesDisponiveis = action.payload;
      })
      .addCase(vincularSituacaoArbitral.pending, (state) => {
        state.gerenciandoSituacao = true;
      })
      .addCase(vincularSituacaoArbitral.fulfilled, (state, action) => {
        state.gerenciandoSituacao = false;
        if (state.processo) {
          state.processo.situacoes = action.payload;
        }
      })
      .addCase(vincularSituacaoArbitral.rejected, (state) => {
        state.gerenciandoSituacao = false;
      })
      .addCase(desvincularSituacaoArbitral.pending, (state) => {
        state.gerenciandoSituacao = true;
      })
      .addCase(desvincularSituacaoArbitral.fulfilled, (state, action) => {
        state.gerenciandoSituacao = false;
        if (state.processo) {
          state.processo.situacoes = action.payload;
        }
      })
      .addCase(desvincularSituacaoArbitral.rejected, (state) => {
        state.gerenciandoSituacao = false;
      })
      .addCase(fetchDocumentosArbitral.pending, (state) => {
        state.loadingDocumentos = true;
      })
      .addCase(fetchDocumentosArbitral.fulfilled, (state, action) => {
        state.loadingDocumentos = false;
        state.documentos = action.payload;
      })
      .addCase(fetchDocumentosArbitral.rejected, (state) => {
        state.loadingDocumentos = false;
      })
      .addCase(removerDocumentoArbitral.pending, (state, action) => {
        state.removingDocumentoId = action.meta.arg.docId;
      })
      .addCase(removerDocumentoArbitral.fulfilled, (state, action) => {
        state.removingDocumentoId = null;
        state.documentos = state.documentos.filter((d) => d.id !== action.payload.docId);
      })
      .addCase(removerDocumentoArbitral.rejected, (state) => {
        state.removingDocumentoId = null;
      })
      .addCase(fetchAndamentosArbitral.pending, (state) => {
        state.loadingAndamentos = true;
        state.errorAndamentos = null;
      })
      .addCase(fetchAndamentosArbitral.fulfilled, (state, action) => {
        state.loadingAndamentos = false;
        if (state.processo) {
          state.processo.andamentos = action.payload;
        }
      })
      .addCase(fetchAndamentosArbitral.rejected, (state, action) => {
        state.loadingAndamentos = false;
        state.errorAndamentos = action.payload ?? 'Não foi possível carregar os andamentos.';
      })
      .addCase(criarAndamentoArbitral.pending, (state) => {
        state.submittingAndamento = true;
      })
      .addCase(criarAndamentoArbitral.fulfilled, (state, action) => {
        state.submittingAndamento = false;
        if (state.processo) {
          state.processo.andamentos = [action.payload, ...state.processo.andamentos];
        }
      })
      .addCase(criarAndamentoArbitral.rejected, (state) => {
        state.submittingAndamento = false;
      })
      .addCase(fetchMensagensChat.pending, (state) => {
        state.loadingChat = true;
      })
      .addCase(fetchMensagensChat.fulfilled, (state, action) => {
        state.loadingChat = false;
        state.mensagensChat = action.payload.mensagens;
      })
      .addCase(fetchMensagensChat.rejected, (state) => {
        state.loadingChat = false;
      })
      .addCase(enviarMensagemChat.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(enviarMensagemChat.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const alreadyExists = state.mensagensChat.some((m) => m.id === action.payload.id);
        if (!alreadyExists) {
          state.mensagensChat.push(action.payload);
        }
      })
      .addCase(enviarMensagemChat.rejected, (state) => {
        state.sendingMessage = false;
      })
      .addCase(marcarMensagemLida.fulfilled, (state, action) => {
        const msg = state.mensagensChat.find((m) => m.id === action.meta.arg.msgId);
        if (msg) msg.lida = true;
        if (state.processo) {
          state.processo.totalMensagensNaoLidas = Math.max(
            0,
            state.processo.totalMensagensNaoLidas - 1,
          );
        }
      })
      .addCase(fetchNaoLidasChat.fulfilled, (state, action) => {
        if (state.processo) {
          state.processo.totalMensagensNaoLidas = action.payload;
        }
      })
      .addCase(fetchSolicitacoesArbitral.pending, (state) => {
        state.loadingSolicitacoes = true;
      })
      .addCase(fetchSolicitacoesArbitral.fulfilled, (state, action) => {
        state.loadingSolicitacoes = false;
        state.solicitacoes = action.payload;
      })
      .addCase(fetchSolicitacoesArbitral.rejected, (state) => {
        state.loadingSolicitacoes = false;
      })
      .addCase(criarSolicitacaoArbitral.pending, (state) => {
        state.submittingSolicitacao = true;
      })
      .addCase(criarSolicitacaoArbitral.fulfilled, (state, action) => {
        state.submittingSolicitacao = false;
        state.solicitacoes = [action.payload, ...state.solicitacoes];
      })
      .addCase(criarSolicitacaoArbitral.rejected, (state) => {
        state.submittingSolicitacao = false;
      })
      .addCase(cancelarSolicitacaoArbitral.fulfilled, (state, action) => {
        const idx = state.solicitacoes.findIndex((s) => s.id === action.payload.id);
        if (idx >= 0) state.solicitacoes[idx] = action.payload;
      })
      .addCase(cancelarProcessoArbitral.pending, (state) => {
        state.cancelandoProcesso = true;
      })
      .addCase(cancelarProcessoArbitral.fulfilled, (state, action) => {
        state.cancelandoProcesso = false;
        state.processo = action.payload.processo;
        if (action.payload.resultado.solicitacao) {
          state.solicitacoes = [action.payload.resultado.solicitacao, ...state.solicitacoes];
        }
      })
      .addCase(cancelarProcessoArbitral.rejected, (state) => {
        state.cancelandoProcesso = false;
      });
  },
});

export const { clearProcesso, appendMensagem, setTyping, clearStaleTypingUsers } =
  arbitralDetalhesSlice.actions;
export const arbitralDetalhesReducer = arbitralDetalhesSlice.reducer;
